/**
 * Utility for extracting display-ready redemption information.
 *
 * This module provides helpers to work with TangoRedemptionInstructions
 * for UI display in the purchase history and redemption components.
 *
 * Supports both:
 * - New format: TangoRedemptionInstructions (structured)
 * - Old format: Raw reward data from rawResponse.reward (for backward compatibility)
 */
import type { TangoRedemptionInstructions } from "@t3/shared";

// Re-export for convenience
export type { TangoRedemptionInstructions } from "@t3/shared";

/**
 * Raw reward data from Tango API response (old format).
 * Used for backward compatibility with existing purchase data.
 */
export interface LegacyRewardData {
  credentials?: Record<string, string>;
  credentialList?: Array<{
    label?: string;
    value?: string;
    type?: string;
    credentialType?: string;
  }>;
  redemptionInstructions?: string;
}

/**
 * Processed redemption information for UI display.
 * This normalizes the TangoRedemptionInstructions for component consumption.
 */
export interface RedemptionInfo {
  /** The security/redemption code (for security code flow) */
  redemptionCode: string | null;
  /** Direct redemption URL (Lowe's type flow) */
  redemptionUrl: string | null;
  /** Security URL where user pastes the redemption code (Walmart/Amazon flow) */
  rewardSecurityUrl: string | null;
  /** Redemption instructions (HTML or text) */
  redemptionInstructions: string | null;
  /** Whether this is a direct redemption flow (has redemptionUrl) */
  hasDirectRedemption: boolean;
  /** Whether this is a security code flow (has both code and security URL) */
  hasSecurityCodeFlow: boolean;
  /** The final URL to use for redemption (redemptionUrl or rewardSecurityUrl) */
  finalRedemptionUrl: string | null;
  /** Code to display to user (only for security code flow, not direct redemption) */
  displayCode: string | null;
}

/**
 * Extracts redemption info from legacy reward data (old format).
 * Used for backward compatibility with existing purchase data.
 */
const extractFromLegacyReward = (
  reward: LegacyRewardData | null | undefined,
  productRedemptionInstructions?: string | null,
): RedemptionInfo => {
  let redemptionCode: string | null = null;
  let redemptionUrl: string | null = null;
  let rewardSecurityUrl: string | null = null;

  // Prioritize credentialList (more structured) over credentials map
  if (reward?.credentialList) {
    for (const cred of reward.credentialList) {
      const credentialType = cred.credentialType?.toLowerCase() || "";
      const label = cred.label?.toLowerCase() || "";

      if (
        (credentialType === "rewardsecuritycode" ||
          label === "rewardsecuritycode") &&
        !redemptionCode
      ) {
        redemptionCode = cred.value ?? null;
      } else if (
        (credentialType === "redemptionurl" || label === "redemptionurl") &&
        !redemptionUrl
      ) {
        redemptionUrl = cred.value ?? null;
      } else if (
        (credentialType === "rewardsecurityurl" ||
          label === "rewardsecurityurl") &&
        !rewardSecurityUrl
      ) {
        rewardSecurityUrl = cred.value ?? null;
      }
    }
  }

  // Fallback to credentials map
  if (reward?.credentials) {
    const creds = reward.credentials;

    if (!redemptionCode) {
      redemptionCode =
        creds["rewardSecurityCode"] ||
        creds["Code"] ||
        creds["code"] ||
        creds["Redemption Code"] ||
        creds["redemption_code"] ||
        creds["Claim Code"] ||
        creds["claim_code"] ||
        null;
    }

    if (!redemptionUrl) {
      redemptionUrl =
        creds["redemptionUrl"] ||
        creds["Redemption URL"] ||
        creds["redemption_url"] ||
        creds["RedemptionUrl"] ||
        null;
    }

    if (!rewardSecurityUrl) {
      rewardSecurityUrl =
        creds["rewardSecurityUrl"] ||
        creds["Reward Security URL"] ||
        creds["reward_security_url"] ||
        creds["RewardSecurityUrl"] ||
        null;
    }
  }

  const hasDirectRedemption = !!redemptionUrl;
  const hasSecurityCodeFlow = !!redemptionCode && !!rewardSecurityUrl;
  const finalRedemptionUrl = redemptionUrl || rewardSecurityUrl;
  // Always display the code if it exists, regardless of flow type
  const displayCode = redemptionCode;
  const redemptionInstructions =
    productRedemptionInstructions || reward?.redemptionInstructions || null;

  return {
    redemptionCode,
    redemptionUrl,
    rewardSecurityUrl,
    redemptionInstructions,
    hasDirectRedemption,
    hasSecurityCodeFlow,
    finalRedemptionUrl,
    displayCode,
  };
};

/**
 * Converts TangoRedemptionInstructions to RedemptionInfo for UI display.
 *
 * @param instructions - The stored redemption instructions from the purchase result
 * @returns Normalized RedemptionInfo for component consumption
 */
export const extractRedemptionInfo = (
  instructions: TangoRedemptionInstructions | null | undefined,
): RedemptionInfo => {
  if (!instructions) {
    return {
      redemptionCode: null,
      redemptionUrl: null,
      rewardSecurityUrl: null,
      redemptionInstructions: null,
      hasDirectRedemption: false,
      hasSecurityCodeFlow: false,
      finalRedemptionUrl: null,
      displayCode: null,
    };
  }

  const {
    flowType,
    redemptionUrl,
    securityCode,
    securityUrl,
    rewardInstructions,
    productInstructions,
  } = instructions;

  const hasDirectRedemption = flowType === "direct";
  const hasSecurityCodeFlow = flowType === "securityCode";
  const redemptionInstructions =
    productInstructions || rewardInstructions || null;
  const finalRedemptionUrl = redemptionUrl || securityUrl || null;
  // Always display the code if it exists, regardless of flow type
  const displayCode = securityCode;

  return {
    redemptionCode: securityCode,
    redemptionUrl,
    rewardSecurityUrl: securityUrl,
    redemptionInstructions,
    hasDirectRedemption,
    hasSecurityCodeFlow,
    finalRedemptionUrl,
    displayCode,
  };
};

/**
 * Extracts redemption info with backward compatibility for old purchase data.
 *
 * Checks for new format (TangoRedemptionInstructions) first, then falls back
 * to extracting from legacy reward data (rawResponse.reward).
 *
 * @param instructions - New format redemption instructions (may be null for old data)
 * @param legacyReward - Legacy reward data from rawResponse.reward
 * @param legacyProductInstructions - Legacy product instructions
 * @returns Normalized RedemptionInfo for component consumption
 */
export const extractRedemptionInfoWithFallback = (
  instructions: TangoRedemptionInstructions | null | undefined,
  legacyReward?: LegacyRewardData | null,
  legacyProductInstructions?: string | null,
): RedemptionInfo => {
  // If we have the new format, use it
  if (instructions) {
    return extractRedemptionInfo(instructions);
  }

  // Fall back to legacy format
  return extractFromLegacyReward(legacyReward, legacyProductInstructions);
};
